// JOBE-0044.6 / JOBE-0044.11: CollectionsRailBlock — bottom block in
// the left rail. Fetches GET /api/collections once on mount; each row
// click appends an `in:<slug>` chip to the filter set via the chip
// vocabulary.
//
// JOBE-0044.11 garnishes:
//   - Header `+ New` action opens NewCollectionModal; on success the
//     provider's refresh() pulls the new row into the rail.
//   - Per-row `×` retire affordance, visible only on principal-owned
//     rows. Confirm prompt before POST /api/collections/{slug}/retire.
//
// Per ADR-0025 §D10, retired collections are excluded server-side; v1
// doesn't surface the lifecycle facet in the rail.
//
// JOBE-0044.10.3: when a CollectionsProvider is mounted on the tree,
// the block consumes that context value so the omni palette's `in:`
// autocomplete and the rail share a single GET per route mount. The
// fallback local fetch stays for test-isolation callers.

import { useContext, useEffect, useState } from 'react';
import { RailRow } from './RailRow.jsx';
import { CollectionsContext, COLLECTIONS_INITIAL } from './CollectionsContext.js';
import { NewCollectionModal } from './NewCollectionModal.jsx';
import { useStore } from '../lib/store.js';

function SkeletonRows() {
  return (
    <ul className="library-rail-block-list" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <li key={i}>
          <div className="library-rail-row library-rail-row-skeleton" aria-hidden="true">
            <span className="skel-line skel-w-70" />
          </div>
        </li>
      ))}
    </ul>
  );
}

// useCollections — when mounted under a CollectionsProvider, short-
// circuit to the context value. Otherwise fall back to the original
// local fetch. Identity-compares against the frozen INITIAL sentinel
// to detect "no provider on the tree."
function useCollections() {
  const ctxValue = useContext(CollectionsContext);
  const providerMounted = ctxValue !== COLLECTIONS_INITIAL;

  const [state, setState] = useState({ status: 'loading', items: [], error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (providerMounted) return undefined;
    let alive = true;
    const ctrl = new AbortController();
    setState({ status: 'loading', items: [], error: null });
    fetch('/api/collections?lifecycle=active', {
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
      .then(async (r) => {
        if (!alive) return;
        if (!r.ok) {
          setState({ status: 'error', items: [], error: `http_${r.status}` });
          return;
        }
        const j = await r.json();
        if (!alive) return;
        const items = Array.isArray(j?.collections) ? j.collections : [];
        setState({ status: 'ready', items, error: null });
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setState({ status: 'error', items: [], error: err?.message || String(err) });
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [providerMounted, tick]);

  if (providerMounted) return ctxValue;
  const localBump = () => setTick((t) => t + 1);
  return { ...state, retry: localBump, refresh: localBump };
}

// principalURNFromMe derives the principal's canonical URN from the
// `/api/me` payload. `subject` carries the `<kind>:<id>` segment
// already (per the schema example: "human:shawn@shawnlower.com"). The
// affordance gate is conservative — `null` here suppresses the
// per-row retire affordance entirely.
function principalURNFromMe(me) {
  if (!me || typeof me.subject !== 'string' || !me.subject) return null;
  return `urn:jobe:${me.subject}`;
}

export function CollectionsRailBlock({ onPickCollection }) {
  const state = useCollections();
  const me = useStore((s) => s.me);
  const principalURN = principalURNFromMe(me);
  const [modalOpen, setModalOpen] = useState(false);
  // retireError surfaces per-collection failures inline. Keyed by slug
  // so a stale error from one row doesn't shadow another.
  const [retireError, setRetireError] = useState(null);

  const handlePick = (collection) => {
    if (onPickCollection) onPickCollection(collection);
  };

  const handleRetire = async (collection) => {
    const slug = collection?.slug;
    const name = collection?.name || slug || collection?.urn;
    if (!slug) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Retire collection "${name}"? It will no longer accept new members.`,
    );
    if (!ok) return;
    setRetireError(null);
    try {
      const r = await fetch(
        `/api/collections/${encodeURIComponent(slug)}/retire`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({}),
        },
      );
      // 200 (retired) and 409 already_retired both converge: the
      // server-side state matches the user's intent; refresh re-syncs.
      if (r.ok || r.status === 409) {
        state.refresh?.();
        return;
      }
      setRetireError({ slug, status: r.status });
    } catch (err) {
      setRetireError({ slug, status: 0, message: err?.message || String(err) });
    }
  };

  return (
    <section className="library-rail-block" aria-label="Collections">
      <header className="library-rail-block-header">
        <h3 className="library-rail-block-title">Collections</h3>
        <button
          type="button"
          className="library-rail-block-new"
          onClick={() => setModalOpen(true)}
          data-testid="collections-new"
          aria-label="New collection"
        >
          + New
        </button>
      </header>

      {state.status === 'loading' && <SkeletonRows />}

      {state.status === 'error' && (
        <p className="library-rail-block-error">
          couldn't load ·{' '}
          <button
            type="button"
            className="library-rail-block-retry"
            onClick={() => state.retry && state.retry()}
          >
            retry
          </button>
        </p>
      )}

      {state.status === 'ready' && state.items.length === 0 && (
        <p className="library-rail-block-empty">nothing</p>
      )}

      {state.status === 'ready' && state.items.length > 0 && (
        <ul className="library-rail-block-list">
          {state.items.map((c) => {
            const owned = principalURN && c.owner?.urn === principalURN;
            return (
              <li key={c.urn} className="library-rail-collection-row">
                <RailRow
                  glyph="·"
                  label={c.name || c.slug}
                  pill={{ kind: 'collection', label: 'collection' }}
                  count="—"
                  title={c.description || c.urn}
                  onClick={() => handlePick(c)}
                />
                {owned && (
                  <button
                    type="button"
                    className="library-rail-collection-retire"
                    aria-label={`Retire collection ${c.name || c.slug}`}
                    title="Retire"
                    data-testid={`collections-retire-${c.slug}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetire(c);
                    }}
                  >
                    ×
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {retireError && (
        <p className="library-rail-block-error" data-testid="collections-retire-error">
          couldn't retire {retireError.slug} ({retireError.status || 'network'})
        </p>
      )}

      {modalOpen && (
        <NewCollectionModal
          onClose={() => setModalOpen(false)}
          onCreated={() => {
            state.refresh?.();
          }}
        />
      )}
    </section>
  );
}
